import { useState, useMemo } from 'react';
import { UserPlus, X, Loader2, Users, List, Search, ChevronDown, Building2, UsersRound, GitMerge } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useListMembers, useAddListMember, useRemoveListMember, useInheritListFromProject } from '@/hooks/use-list-members';
import { useUsers } from '@/hooks/use-users';
import { toast } from '@/components/ui/toaster';
import { getInitials } from '@/lib/utils';
import type { GlobalRole, ListMemberAccessSource } from '@/types';

type ApiErrorLike = { response?: { data?: { message?: string } } };

const ROLE_LABELS: Record<GlobalRole, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  user: 'User',
  client: 'Client',
};

const ACCESS_SOURCE_LABELS: Record<ListMemberAccessSource, string> = {
  direct: 'Direct',
  project: 'From Project',
  team: 'From Team',
};

interface ListMembershipDialogProps {
  listId: string;
  listName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ListMembershipDialog({
  listId,
  listName,
  open,
  onOpenChange,
}: ListMembershipDialogProps) {
  const { data: members = [], isLoading: membersLoading } = useListMembers(listId);
  const { data: allUsers = [] } = useUsers();
  
  const addMember = useAddListMember();
  const removeMember = useRemoveListMember();
  const inheritFromProject = useInheritListFromProject();

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const memberIds = new Set(members.map((m) => m.userId));
  const availableUsers = allUsers.filter((u) => !memberIds.has(u.id) && u.isActive);

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return availableUsers;
    const query = userSearchQuery.toLowerCase();
    return availableUsers.filter(
      (u) =>
        u.displayName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
    );
  }, [availableUsers, userSearchQuery]);

  const selectedUser = allUsers.find((u) => u.id === selectedUserId);

  const handleAddMember = async () => {
    if (!selectedUserId) return;

    try {
      await addMember.mutateAsync({ listId, userId: selectedUserId });
      setSelectedUserId('');
      toast({ title: 'Member added to list' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to add member',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleInheritFromProject = async () => {
    try {
      await inheritFromProject.mutateAsync(listId);
      toast({ title: 'List now inherits all project and team members' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to inherit from project',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveMember = async (userId: string, accessSource?: ListMemberAccessSource) => {
    try {
      await removeMember.mutateAsync({ listId, userId });
      const message = accessSource === 'direct' 
        ? 'Member removed from list'
        : 'Member excluded from this list';
      toast({ title: message });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to remove member',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const getAccessSourceIcon = (source?: ListMemberAccessSource) => {
    switch (source) {
      case 'project':
        return <Building2 className="h-3 w-3" />;
      case 'team':
        return <UsersRound className="h-3 w-3" />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <List className="h-5 w-5" />
            List Access
          </DialogTitle>
          <DialogDescription>
            Manage who can access {listName}. You can remove users from this list
            without removing them from the project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Inherit from project */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className="w-full gap-2 border-purple-500 text-purple-500 hover:bg-purple-500/15 hover:border-purple-400 hover:text-purple-400"
                onClick={handleInheritFromProject}
                disabled={inheritFromProject.isPending}
              >
                {inheritFromProject.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GitMerge className="h-4 w-4" />
                )}
                Inherit from project
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              Restore access for all project and team members. Removes any per-list exclusions so this list matches the project again.
            </TooltipContent>
          </Tooltip>

          {/* Add member */}
          <div className="flex gap-2">
            <Popover open={userPickerOpen} onOpenChange={setUserPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={userPickerOpen}
                  className="flex-1 justify-between"
                >
                  {selectedUser ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={selectedUser.avatarUrl || undefined} />
                        <AvatarFallback className="text-[10px]">
                          {getInitials(selectedUser.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <span>{selectedUser.displayName}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Search users...</span>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <div className="flex items-center border-b px-3">
                  <Search className="h-4 w-4 shrink-0 opacity-50" />
                  <Input
                    placeholder="Search by name or email..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="border-0 focus-visible:ring-0 h-10"
                  />
                </div>
                <ScrollArea className="max-h-[200px]">
                  {filteredUsers.length === 0 ? (
                    <div className="p-4 text-sm text-center text-muted-foreground">
                      {availableUsers.length === 0
                        ? 'All users already have access'
                        : 'No users found'}
                    </div>
                  ) : (
                    <div className="p-1">
                      {filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setUserPickerOpen(false);
                            setUserSearchQuery('');
                          }}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted text-left"
                        >
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={user.avatarUrl || undefined} />
                            <AvatarFallback className="text-[10px]">
                              {getInitials(user.displayName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{user.displayName}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
            <Button
              onClick={handleAddMember}
              disabled={!selectedUserId || addMember.isPending}
              className="gap-2"
            >
              {addMember.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Add
            </Button>
          </div>

          {/* Members list */}
          <div className="border rounded-lg">
            <div className="p-3 border-b bg-muted/50">
              <span className="text-sm font-medium">
                Members ({members.length})
              </span>
            </div>
            <ScrollArea className="h-[300px]">
              {membersLoading ? (
                <div className="p-4 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : members.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No members in this list</p>
                  <p className="text-xs mt-1">
                    Add users or assign users to the project.
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.user.avatarUrl || undefined} />
                          <AvatarFallback className="text-xs">
                            {getInitials(member.user.displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {member.user.displayName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.user.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {member.accessSource && member.accessSource !== 'direct' && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary" className="text-xs gap-1">
                                {getAccessSourceIcon(member.accessSource)}
                                {ACCESS_SOURCE_LABELS[member.accessSource]}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              {member.accessSource === 'project'
                                ? 'Access inherited from project membership'
                                : 'Access inherited from team membership'}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {ROLE_LABELS[member.user.role]}
                        </Badge>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRemoveMember(member.userId, member.accessSource)}
                              disabled={removeMember.isPending}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {member.accessSource === 'direct'
                              ? 'Remove from list'
                              : 'Exclude from this list'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <p className="text-xs text-muted-foreground">
            Members with "From Project" or "From Team" tags have inherited access.
            Removing them will exclude them from this list only.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
